import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mapQuickAccessHref } from "../map/MapQuickAccess";

export const PIAZZA_MOBILE_MQ = "(max-width: 1023px)";

export const MAP_MACRO_FILTERS = [
  {
    id: "montagna",
    label: "Montagna",
    emoji: "⛰️",
    query: "mode=terra&macro=montagna",
  },
  {
    id: "terra",
    label: "Terra",
    emoji: "🌿",
    query: "mode=terra&macro=terra",
  },
  {
    id: "mare",
    label: "Mare",
    emoji: "🌊",
    query: "mode=mare",
  },
];

export function useIsPiazzaMobileViewport() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(PIAZZA_MOBILE_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(PIAZZA_MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  return isMobile;
}

function readStoredMacro() {
  try {
    const v = (sessionStorage.getItem("6amico_map_esperienza") || "").trim().toLowerCase();
    if (v === "montagna" || v === "terra" || v === "mare") return v;
  } catch {
    /* ignore */
  }
  return "terra";
}

function buildEmbedSrc(macroId, comuneSlug) {
  const filter = MAP_MACRO_FILTERS.find((f) => f.id === macroId) || MAP_MACRO_FILTERS[1];
  const params = new URLSearchParams(filter.query);
  params.set("embed", "1");
  if (comuneSlug) params.set("comune", comuneSlug);
  return `/esplora-la-mappa?${params.toString()}`;
}

/**
 * Overlay mappa fullscreen su /piazze (solo viewport &lt; 1024px).
 * Filtri Montagna / Terra / Mare + iframe embed.
 */
export function PiazzaMobileMapOverlay({
  active = false,
  macroId,
  onMacroChange,
  comuneSlug = null,
}) {
  const src = useMemo(
    () => buildEmbedSrc(macroId || readStoredMacro(), comuneSlug),
    [macroId, comuneSlug],
  );

  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);

  if (!active) return null;

  return (
    <div
      className="piazza-mobile-map-overlay"
      data-testid="piazza-mobile-map-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Mappa territoriale"
    >
      <div className="piazza-mobile-map-overlay__filters" role="tablist" aria-label="Filtri mappa">
        {MAP_MACRO_FILTERS.map((f) => {
          const isActive = (macroId || "terra") === f.id;
          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`piazza-mobile-map-overlay__chip${isActive ? " is-active" : ""}`}
              data-testid={`piazza-mobile-map-filter-${f.id}`}
              onClick={() => {
                onMacroChange?.(f.id);
                try {
                  sessionStorage.setItem("6amico_map_esperienza", f.id);
                } catch {
                  /* ignore */
                }
              }}
            >
              <span aria-hidden>{f.emoji}</span> {f.label}
            </button>
          );
        })}
      </div>
      <iframe
        key={src}
        className="piazza-mobile-map-overlay__frame"
        src={src}
        title="Esplora la Mappa"
        data-testid="piazza-mobile-map-iframe"
        allow="geolocation; fullscreen"
      />
    </div>
  );
}

/**
 * FAB fisso bottom-center: scambia feed Piazza ↔ mappa (solo smartphone).
 */
export function PiazzaMobileVistaFab({ mostraMappaMobile, onToggle }) {
  return (
    <button
      type="button"
      className={`piazza-mobile-vista-fab${mostraMappaMobile ? " piazza-mobile-vista-fab--over-map" : ""}`}
      data-testid="piazza-mobile-vista-fab"
      aria-pressed={mostraMappaMobile}
      onClick={onToggle}
    >
      {mostraMappaMobile ? "💬 Vedi i Post della Piazza" : "🗺️ Esplora la Mappa"}
    </button>
  );
}

/**
 * FAB su /esplora-la-mappa → torna alla Piazza (mobile), preservando query utili.
 */
export function EsploraMappaToPiazzaFab() {
  const isMobile = useIsPiazzaMobileViewport();
  if (!isMobile) return null;

  return (
    <Link
      to="/piazze"
      className="piazza-mobile-vista-fab piazza-mobile-vista-fab--over-map"
      data-testid="esplora-to-piazza-fab"
      replace={false}
    >
      💬 Vedi i Post della Piazza
    </Link>
  );
}

/** Helper: da header — apri vista mappa in-page su /piazze. */
export function piazzaVistaMappaPath(search = "") {
  const p = new URLSearchParams(typeof search === "string" ? search : "");
  p.set("vista", "mappa");
  const q = p.toString();
  return q ? `/piazze?${q}` : "/piazze?vista=mappa";
}

export function piazzaVistaFeedPath(search = "") {
  const p = new URLSearchParams(typeof search === "string" ? search : "");
  p.delete("vista");
  const q = p.toString();
  return q ? `/piazze?${q}` : "/piazze";
}

/** Naviga alla mappa full-page (desktop / fuori Piazza). */
export function useNavigateToMap() {
  const navigate = useNavigate();
  return (pathname, search) => {
    navigate(mapQuickAccessHref(pathname, search));
  };
}

export { readStoredMacro, buildEmbedSrc };
